import { Controller } from "@hotwired/stimulus"

// Faceplate & silkscreen-strip furniture. One controller, three small jobs,
// dispatched by what the element is:
//
//   1. Calibration ruler (.ruler-zone with a .sweep-dot): run ONE sweep on
//      connect. Reduced motion is handled in CSS (the dot is parked at the end);
//      the .run class is simply inert there.
//   2. SWEEP rules (.rule): reveal once via IntersectionObserver. The transition
//      lives behind a prefers-reduced-motion gate in CSS.
//   3. Copy latches ([data-copy] / [data-copy-target]): copy to clipboard, flash
//      the LED, swap the label to "Copied". Line-number (.no) spans are stripped
//      from copied code.
//
// Audio (Monitor Bus tick) is intentionally omitted — deferred to a later PR.
export default class extends Controller {
  connect() {
    const el = this.element
    if (el.matches("[data-copy],[data-copy-target]")) return this._initCopy()
    if (el.querySelector(".sweep-dot")) return this._initRuler()
    if (el.matches(".rule")) return this._initRule()
  }

  disconnect() {
    if (this._observer) this._observer.disconnect()
    if (this._onClick) this.element.removeEventListener("click", this._onClick)
    if (this._restoreTimer) clearTimeout(this._restoreTimer)
  }

  _initRuler() {
    this.element.querySelector(".sweep-dot")?.classList.add("run")
  }

  _initRule() {
    if (typeof IntersectionObserver !== "function") {
      this.element.classList.add("is-in") // no observer: reveal immediately
      return
    }
    this._observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-in")
          this._observer.unobserve(entry.target)
        }
      })
    }, { threshold: 0 })
    this._observer.observe(this.element)
  }

  _initCopy() {
    this._onClick = async () => {
      try {
        await navigator.clipboard.writeText(this._copyText())
      } catch {
        // Clipboard unavailable — leave the source in place, no error surfaced.
      }
      const led = this.element.querySelector(".led")
      const label = this.element.lastChild
      led?.classList.add("on")
      const previous = label ? label.textContent : null
      if (label) label.textContent = "Copied"
      this._restoreTimer = setTimeout(() => {
        led?.classList.remove("on")
        if (label) label.textContent = previous
      }, 1100)
    }
    this.element.addEventListener("click", this._onClick)
  }

  _copyText() {
    const el = this.element
    if (el.dataset.copy != null) return el.dataset.copy
    const source = document.getElementById(el.dataset.copyTarget)
    if (!source) return ""
    const clone = source.cloneNode(true)
    clone.querySelectorAll(".no").forEach((node) => node.remove()) // no line numbers
    return clone.textContent
  }
}
